import { useCallback, useEffect, useRef, useState } from 'react';
import { MOTION_DURATIONS } from './kit/tokens.js';

/** Shared dwell time before any full-card hover preview opens. */
export const FULL_CARD_HOVER_OPEN_DELAY_MS = 200;
export const HOVER_POPOVER_EXIT_MS = MOTION_DURATIONS.quick;

export interface HoverPopoverController<T> {
  /** The currently hovered target (anchor info), or undefined when closed. */
  value: T | undefined;
  /** True while the mounted popover is playing its exit animation. */
  closing: boolean;
  /**
   * Schedule `next` to open, cancelling any pending open or close. Pass
   * `immediate` to skip the dwell — for handing an already-open popover from one
   * anchor to a neighbouring one, where the dwell reads as a flicker rather than
   * as protection against an accidental preview.
   */
  open: (next: T, immediate?: boolean) => void;
  /** Cancel a pending close (e.g. the pointer entered the popover itself). */
  cancelClose: () => void;
  /** Close after a short grace period so the pointer can travel to the popover. */
  scheduleClose: () => void;
  /** Close immediately (drag start, dialog opened, unmount). */
  closeNow: () => void;
}

export interface HoverPopoverOptions<T = unknown> {
  /** Delay before the popover appears. Useful for full-card previews. */
  openDelayMs?: number;
  /** Grace period while the pointer travels from the anchor to the popover. */
  closeDelayMs?: number;
  /** Immediately dismiss when browsing turns into viewport scrolling or drag. */
  dismissOnScrollOrDrag?: boolean;
  /**
   * Whether two targets are the same anchor. Callers that re-open on every
   * pointer move pass a fresh object each time; without this the shown popover
   * would be re-set (and its owner re-rendered) on every move.
   */
  isSame?: (current: T, next: T) => boolean;
}

/**
 * Instant dismissers of every popover currently on screen, across all
 * controllers. Full-card previews are one at a time: once one is showing, the
 * player is reading cards, so the next anchor they point at takes over at once —
 * no second dwell, and no stale preview left hanging over the card they left.
 */
const shownPopovers = new Set<() => void>();

/**
 * Open/close state for an interactive hover popover. Callers may add an opening
 * delay to avoid accidental previews. The short close delay plus `cancelClose`
 * bridge the gap between anchor and popover so the pointer can move onto it and
 * click instead of the popover vanishing on anchor mouseleave.
 */
export function useHoverPopover<T>({
  openDelayMs = 0,
  closeDelayMs = 140,
  dismissOnScrollOrDrag = false,
  isSame = Object.is,
}: HoverPopoverOptions<T> = {}): HoverPopoverController<T> {
  const [value, setValue] = useState<T | undefined>(undefined);
  const [closing, setClosing] = useState(false);
  const valueRef = useRef(value);
  valueRef.current = value;
  const isSameRef = useRef(isSame);
  isSameRef.current = isSame;
  const openTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const exitTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const clear = useCallback((): void => {
    if (openTimer.current !== undefined) {
      clearTimeout(openTimer.current);
      openTimer.current = undefined;
    }
    if (closeTimer.current !== undefined) {
      clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
    if (exitTimer.current !== undefined) {
      clearTimeout(exitTimer.current);
      exitTimer.current = undefined;
    }
  }, []);
  useEffect(() => clear, [clear]);
  const open = useCallback((next: T, immediate = false): void => {
    const shown = valueRef.current;
    if (shown !== undefined && isSameRef.current(shown, next)) {
      // Still over the card it describes: keep it, and keep it from leaving.
      clear();
      setClosing(false);
      return;
    }
    clear();
    setClosing(false);
    // Hand-off: some preview (this one or another card's) is already showing.
    if (immediate || openDelayMs === 0 || shown !== undefined || shownPopovers.size > 0) {
      setValue(next);
      return;
    }
    openTimer.current = setTimeout(() => {
      openTimer.current = undefined;
      setValue(next);
    }, openDelayMs);
  }, [clear, openDelayMs]);
  const cancelClose = useCallback((): void => {
    if (closeTimer.current !== undefined) {
      clearTimeout(closeTimer.current);
      closeTimer.current = undefined;
    }
    if (exitTimer.current !== undefined) {
      clearTimeout(exitTimer.current);
      exitTimer.current = undefined;
    }
    setClosing(false);
  }, []);
  const scheduleClose = useCallback((): void => {
    clear();
    closeTimer.current = setTimeout(() => {
      closeTimer.current = undefined;
      if (value === undefined) return;
      setClosing(true);
      exitTimer.current = setTimeout(() => {
        exitTimer.current = undefined;
        setValue(undefined);
        setClosing(false);
      }, HOVER_POPOVER_EXIT_MS);
    }, closeDelayMs);
  }, [clear, closeDelayMs, value]);
  const closeNow = useCallback((): void => {
    clear();
    setValue(undefined);
    setClosing(false);
  }, [clear]);

  // One preview on screen at a time: taking over dismisses whatever showed before.
  useEffect(() => {
    if (value === undefined) return undefined;
    const dismiss = (): void => {
      clear();
      setValue(undefined);
      setClosing(false);
    };
    for (const other of shownPopovers) other();
    shownPopovers.add(dismiss);
    return () => {
      shownPopovers.delete(dismiss);
    };
  }, [value, clear]);

  useEffect(() => {
    if (!dismissOnScrollOrDrag) return undefined;
    // Scroll does not bubble, so capture it from every scrollable ancestor.
    // Native dragstart complements the app's pointer-driven drags, whose
    // handlers also call closeNow as soon as the carry threshold is crossed.
    const dismiss = (): void => closeNow();
    window.addEventListener('scroll', dismiss, true);
    document.addEventListener('dragstart', dismiss, true);
    return () => {
      window.removeEventListener('scroll', dismiss, true);
      document.removeEventListener('dragstart', dismiss, true);
    };
  }, [closeNow, dismissOnScrollOrDrag]);

  return { value, closing, open, cancelClose, scheduleClose, closeNow };
}
